import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import AppNavbar from '../components/AppNavbar';
import ClassificationBanner from '../components/ClassificationBanner';
import Header from '../components/Header';
import RequestClassificationField, { classificationIsCui } from '../components/RequestClassificationField';
import SchemaBodyFields from '../components/SchemaBodyFields';
import SideMenu from '../components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import axiosInstance from '../utils/axiosInstance';
import { formatCapabilityLabel } from '../utils/capabilityContract';

import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

function fieldName(field) {
  return field?.name || field?.key || '';
}

function fieldLabel(field) {
  return field?.label || field?.display_name || fieldName(field);
}

function fieldType(field) {
  return String(field?.type || 'text').toLowerCase();
}

function isBlank(value) {
  return value === null || typeof value === 'undefined' || value === '' || (Array.isArray(value) && value.length === 0);
}

function normalizeStructuredValue(type, value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value;
  }
  const text = String(value || '').trim();
  if (!text) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed;
    }
  } catch (_error) {
    if (type === 'physical_item') {
      return { description: text };
    }
    if (type === 'reference') {
      return { reference: text };
    }
  }
  return { value: text };
}

function normalizeValuesForSubmit(fields, values, uploadedArtifacts) {
  return (fields || []).reduce((acc, field) => {
    const name = fieldName(field);
    if (!name) {
      return acc;
    }
    const type = fieldType(field);
    const uploadedValue = uploadedArtifacts[name];
    const value = typeof uploadedValue !== 'undefined' ? uploadedValue : values[name];
    if (isBlank(value)) {
      return acc;
    }
    if (type === 'number' || type === 'range') {
      const numberValue = Number(value);
      if (!Number.isNaN(numberValue)) {
        acc[name] = numberValue;
      }
      return acc;
    }
    if (['structured_data', 'physical_item', 'reference', 'key_value_pairs'].includes(type)) {
      const structured = normalizeStructuredValue(type, value);
      if (structured) {
        acc[name] = structured;
      }
      return acc;
    }
    acc[name] = value;
    return acc;
  }, {});
}

function fileList(value) {
  if (!value) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

function businessUnitLabel(unit) {
  const parent = unit?.parent_partner?.display_name || unit?.parent_partner?.partner_code;
  const unitName = unit?.display_name || unit?.business_unit_code || unit?.business_unit_id;
  return parent ? `${parent} / ${unitName}` : unitName;
}

function capabilityProviderLabel(capability) {
  return businessUnitLabel(capability?.provider_business_unit || {});
}

function schemaFields(contract, key) {
  return contract?.effective_request_schema?.[key] || [];
}

function statusColor(isComplete) {
  return isComplete ? 'success' : 'warning';
}

function localToday() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function reviewValue(value) {
  if (Array.isArray(value)) {
    return value.map((item) => item?.name || item).filter(Boolean).join(', ');
  }
  if (value && typeof value === 'object') {
    return value.name || JSON.stringify(value);
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return String(value ?? '');
}

function hasDraftValues(item) {
  return Boolean(
    Object.keys(item.sharedValues || {}).length ||
    Object.keys(item.capabilityValues || {}).length ||
    Object.keys(item.fileValues || {}).length
  );
}

function CapabilityPickerDialog({
  open,
  requestingBusinessUnitId,
  selectedIds,
  onClose,
  onAdd,
}) {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !requestingBusinessUnitId) {
      setOptions([]);
      return undefined;
    }

    let isMounted = true;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axiosInstance.get('/requests/capabilities', {
          params: {
            requesting_business_unit_id: requestingBusinessUnitId,
            q: query.trim() || undefined,
            limit: 50,
          },
        });
        if (isMounted) {
          setOptions(Array.isArray(response.data?.capabilities) ? response.data.capabilities : []);
        }
      } catch (err) {
        if (isMounted) {
          setError(err?.response?.data?.message || err?.message || 'Unable to load capabilities.');
          setOptions([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }, 250);

    return () => {
      isMounted = false;
      window.clearTimeout(timer);
    };
  }, [open, query, requestingBusinessUnitId]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Add Capability</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            fullWidth
            InputProps={{ startAdornment: <SearchRoundedIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
          />
          {error ? <Alert severity="error">{error}</Alert> : null}
          {loading ? (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="body2">Loading capabilities...</Typography>
            </Stack>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Capability</TableCell>
                  <TableCell>Need Category</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Acceptance</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {options.length ? options.map((capability) => {
                  const id = capability.capability_definition_id || capability.capability_id;
                  const selected = selectedIds.has(id);
                  return (
                    <TableRow key={id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {capability.capability_name || 'Capability'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatCapabilityLabel(capability.capability_type || capability.fulfillment_mode || 'manual')}
                        </Typography>
                      </TableCell>
                      <TableCell>{capability.category_display_name || capability.category_name || 'N/A'}</TableCell>
                      <TableCell>{capabilityProviderLabel(capability)}</TableCell>
                      <TableCell>{formatCapabilityLabel(capability.acceptance_mode || 'manual')}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant={selected ? 'outlined' : 'contained'}
                          disabled={selected}
                          startIcon={<AddRoundedIcon />}
                          onClick={() => onAdd(capability)}
                        >
                          {selected ? 'Added' : 'Add'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                }) : (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">No capabilities found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function RequestedCapabilityFormCard({
  item,
  index,
  errors,
  submitting,
  onChange,
  onFileChange,
  onRemove,
}) {
  const sharedFields = schemaFields(item.contract, 'shared_fields');
  const additionalFields = schemaFields(item.contract, 'additional_fields');
  const isComplete = Object.keys(errors?.shared || {}).length === 0 && Object.keys(errors?.capability || {}).length === 0;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              {index + 1}. {item.contract?.capability_name || item.capability?.capability_name || 'Capability'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {businessUnitLabel(item.contract?.provider_business_unit || item.capability?.provider_business_unit || {})}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Chip size="small" color={statusColor(isComplete)} label={isComplete ? 'Complete' : 'Incomplete'} />
            <Button
              size="small"
              color="error"
              variant="outlined"
              startIcon={<DeleteRoundedIcon />}
              onClick={() => onRemove(item)}
              disabled={submitting}
            >
              Remove
            </Button>
          </Stack>
        </Stack>
        <Divider />
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
            Shared Need Information
          </Typography>
          <SchemaBodyFields
            fields={sharedFields}
            values={item.sharedValues}
            fileValues={item.fileValues}
            errors={errors?.shared || {}}
            disabled={submitting}
            onChange={(field, value) => onChange(item, 'sharedValues', field, value)}
            onFileChange={(field, value) => onFileChange(item, field, value)}
          />
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
            Provider-Specific Information
          </Typography>
          {additionalFields.length ? (
            <SchemaBodyFields
              fields={additionalFields}
              values={item.capabilityValues}
              fileValues={item.fileValues}
              errors={errors?.capability || {}}
              disabled={submitting}
              onChange={(field, value) => onChange(item, 'capabilityValues', field, value)}
              onFileChange={(field, value) => onFileChange(item, field, value)}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">No provider-specific inputs.</Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}

export default function RequestCreatePage(props) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [error, setError] = useState('');
  const [businessUnits, setBusinessUnits] = useState([]);
  const [needCategories, setNeedCategories] = useState([]);
  const [needFields, setNeedFields] = useState([]);
  const [needValues, setNeedValues] = useState({});
  const [needFileValues, setNeedFileValues] = useState({});
  const [needFieldErrors, setNeedFieldErrors] = useState({});
  const [needSchemaLoading, setNeedSchemaLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [capabilityErrors, setCapabilityErrors] = useState({});
  const [generalFiles, setGeneralFiles] = useState([]);
  const [form, setForm] = useState({
    requesting_business_unit_id: '',
    need_category_id: '',
    classification: '',
    title: '',
    description: '',
    needed_by: '',
    priority: '',
    general_notes: '',
  });
  const [selectedCapabilities, setSelectedCapabilities] = useState([]);

  useEffect(() => {
    let isMounted = true;
    async function loadRequestOptions() {
      setLoading(true);
      setError('');
      try {
        const [businessUnitResponse, needCategoryResponse] = await Promise.all([
          axiosInstance.get('/requests/business-units'),
          axiosInstance.get('/requests/need-categories'),
        ]);
        const nextBusinessUnits = Array.isArray(businessUnitResponse.data?.business_units)
          ? businessUnitResponse.data.business_units
          : [];
        if (!isMounted) {
          return;
        }
        setBusinessUnits(nextBusinessUnits);
        setNeedCategories(Array.isArray(needCategoryResponse.data) ? needCategoryResponse.data : []);
        setForm((current) => ({
          ...current,
          requesting_business_unit_id: current.requesting_business_unit_id || (nextBusinessUnits.length === 1 ? nextBusinessUnits[0].business_unit_id : ''),
        }));
      } catch (err) {
        if (isMounted) {
          setError(err?.response?.data?.message || err?.message || 'Unable to load business units.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadRequestOptions();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    async function loadNeedSchema() {
      setNeedFields([]);
      setNeedValues({});
      setNeedFileValues({});
      setNeedFieldErrors({});
      if (!form.need_category_id) {
        return;
      }
      setNeedSchemaLoading(true);
      setError('');
      try {
        const response = await axiosInstance.get(
          `/requests/discovery/${encodeURIComponent(form.need_category_id)}/schema`,
        );
        if (isMounted) {
          setNeedFields(Array.isArray(response.data?.shared_fields) ? response.data.shared_fields : []);
        }
      } catch (err) {
        if (isMounted) {
          setError(err?.response?.data?.message || err?.message || 'Unable to load Need Category requirements.');
        }
      } finally {
        if (isMounted) {
          setNeedSchemaLoading(false);
        }
      }
    }

    loadNeedSchema();
    return () => {
      isMounted = false;
    };
  }, [form.need_category_id]);

  const selectedIds = useMemo(
    () => new Set(selectedCapabilities.map((item) => item.capability_definition_id)),
    [selectedCapabilities],
  );

  const updateCapabilityValue = (item, bucket, field, value) => {
    const name = fieldName(field);
    setSelectedCapabilities((current) => current.map((candidate) => (
      candidate.capability_definition_id === item.capability_definition_id
        ? { ...candidate, [bucket]: { ...candidate[bucket], [name]: value } }
        : candidate
    )));
  };

  const updateCapabilityFile = (item, field, value) => {
    const name = fieldName(field);
    setSelectedCapabilities((current) => current.map((candidate) => (
      candidate.capability_definition_id === item.capability_definition_id
        ? { ...candidate, fileValues: { ...candidate.fileValues, [name]: value } }
        : candidate
    )));
  };

  const addCapability = async (capability) => {
    const capabilityDefinitionId = capability.capability_definition_id || capability.capability_id;
    if (!capabilityDefinitionId || selectedIds.has(capabilityDefinitionId)) {
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const response = await axiosInstance.get(`/capabilities/${encodeURIComponent(capabilityDefinitionId)}/request-schema`);
      setSelectedCapabilities((current) => [
        ...current,
        {
          capability_definition_id: capabilityDefinitionId,
          capability,
          contract: response.data || {},
          sharedValues: {},
          capabilityValues: {},
          fileValues: {},
        },
      ]);
      setPickerOpen(false);
      setReviewing(false);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to add capability.');
    } finally {
      setSubmitting(false);
    }
  };

  const removeCapability = (item) => {
    if (hasDraftValues(item) && !window.confirm('Remove this capability and its draft request information?')) {
      return;
    }
    setSelectedCapabilities((current) => current.filter((candidate) => (
      candidate.capability_definition_id !== item.capability_definition_id
    )));
    setReviewing(false);
  };

  const validateRequiredFields = (fields, values, fileValues) => {
    const nextErrors = {};
    (fields || []).forEach((field) => {
      if (!field?.required) {
        return;
      }
      const name = fieldName(field);
      const value = fieldType(field) === 'file' ? fileValues[name] : values[name];
      if (isBlank(value)) {
        nextErrors[name] = `${field?.label || field?.display_name || name} is required.`;
      }
    });
    return nextErrors;
  };

  const validateForm = () => {
    const nextFormErrors = {};
    const nextCapabilityErrors = {};
    let nextNeedFieldErrors = {};
    if (!form.requesting_business_unit_id) {
      nextFormErrors.requesting_business_unit_id = 'Requesting Business Unit is required.';
    }
    if (!form.classification) {
      nextFormErrors.classification = 'Content Classification is required.';
    }
    if (!form.title.trim()) {
      nextFormErrors.title = 'Request Title is required.';
    }
    if (!form.description.trim()) {
      nextFormErrors.description = 'Description is required.';
    }
    if (!selectedCapabilities.length) {
      if (!form.need_category_id) {
        nextFormErrors.need_category_id = 'Need Category is required when no Capability is selected.';
      }
      nextNeedFieldErrors = validateRequiredFields(needFields, needValues, needFileValues);
    }

    selectedCapabilities.forEach((item) => {
      const sharedErrors = validateRequiredFields(
        schemaFields(item.contract, 'shared_fields'),
        item.sharedValues,
        item.fileValues,
      );
      const capabilityFieldErrors = validateRequiredFields(
        schemaFields(item.contract, 'additional_fields'),
        item.capabilityValues,
        item.fileValues,
      );
      if (Object.keys(sharedErrors).length || Object.keys(capabilityFieldErrors).length) {
        nextCapabilityErrors[item.capability_definition_id] = {
          shared: sharedErrors,
          capability: capabilityFieldErrors,
        };
      }
    });

    setFormErrors(nextFormErrors);
    setCapabilityErrors(nextCapabilityErrors);
    setNeedFieldErrors(nextNeedFieldErrors);
    return Object.keys(nextFormErrors).length === 0
      && Object.keys(nextCapabilityErrors).length === 0
      && Object.keys(nextNeedFieldErrors).length === 0;
  };

  const uploadGeneralFiles = async () => {
    const artifactIds = [];
    for (const file of generalFiles) {
      const uploadResponse = await axiosInstance.post('/requests/upload', {
        requesting_business_unit_id: form.requesting_business_unit_id,
        is_cui: classificationIsCui(form.classification),
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        size_bytes: file.size,
      });
      const uploadUrl = uploadResponse.data?.upload_url;
      if (!uploadUrl) {
        throw new Error('Upload URL was not returned.');
      }
      const s3Response = await fetch(uploadUrl, {
        method: uploadResponse.data?.method || 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!s3Response.ok) {
        throw new Error(`Upload failed for ${file.name}.`);
      }
      artifactIds.push(uploadResponse.data?.artifact_id || uploadResponse.data?.file_id);
    }
    return artifactIds.filter(Boolean);
  };

  const uploadFilesForCapability = async (item, fields, source) => {
    const uploaded = {};
    for (const field of fields) {
      if (fieldType(field) !== 'file') {
        continue;
      }
      const name = fieldName(field);
      const files = fileList(item.fileValues[name]);
      if (!files.length) {
        continue;
      }
      const artifactIds = [];
      for (const file of files) {
        const uploadResponse = await axiosInstance.post(
          `/capabilities/${encodeURIComponent(item.capability_definition_id)}/request/upload`,
          {
            requesting_business_unit_id: form.requesting_business_unit_id,
            is_cui: classificationIsCui(form.classification),
            field_key: name,
            source,
            filename: file.name,
            content_type: file.type || 'application/octet-stream',
            size_bytes: file.size,
          },
        );
        const uploadUrl = uploadResponse.data?.upload_url;
        if (!uploadUrl) {
          throw new Error('Upload URL was not returned.');
        }
        const s3Response = await fetch(uploadUrl, {
          method: uploadResponse.data?.method || 'PUT',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        });
        if (!s3Response.ok) {
          throw new Error(`Upload failed for ${file.name}.`);
        }
        artifactIds.push(uploadResponse.data?.artifact_id || uploadResponse.data?.file_id);
      }
      uploaded[name] = field.multiple ? artifactIds : artifactIds[0];
    }
    return uploaded;
  };

  const uploadFilesForNeed = async () => {
    const uploaded = {};
    for (const field of needFields) {
      if (fieldType(field) !== 'file') {
        continue;
      }
      const name = fieldName(field);
      const files = fileList(needFileValues[name]);
      if (!files.length) {
        continue;
      }
      const artifactIds = [];
      for (const file of files) {
        const uploadResponse = await axiosInstance.post('/requests/upload', {
          requesting_business_unit_id: form.requesting_business_unit_id,
          is_cui: classificationIsCui(form.classification),
          field_key: name,
          filename: file.name,
          content_type: file.type || 'application/octet-stream',
          size_bytes: file.size,
        });
        const uploadUrl = uploadResponse.data?.upload_url;
        if (!uploadUrl) {
          throw new Error('Upload URL was not returned.');
        }
        const s3Response = await fetch(uploadUrl, {
          method: uploadResponse.data?.method || 'PUT',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file,
        });
        if (!s3Response.ok) {
          throw new Error(`Upload failed for ${file.name}.`);
        }
        artifactIds.push(uploadResponse.data?.artifact_id || uploadResponse.data?.file_id);
      }
      uploaded[name] = field.multiple ? artifactIds.filter(Boolean) : artifactIds.find(Boolean);
    }
    return uploaded;
  };

  const submitRequest = async () => {
    if (!validateForm()) {
      setReviewing(false);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const generalArtifactIds = await uploadGeneralFiles();
      const capabilities = [];
      for (const item of selectedCapabilities) {
        const sharedFields = schemaFields(item.contract, 'shared_fields');
        const additionalFields = schemaFields(item.contract, 'additional_fields');
        const sharedUploads = await uploadFilesForCapability(item, sharedFields, 'shared');
        const capabilityUploads = await uploadFilesForCapability(item, additionalFields, 'capability');
        capabilities.push({
          capability_definition_id: item.capability_definition_id,
          shared_request_values: normalizeValuesForSubmit(sharedFields, item.sharedValues, sharedUploads),
          capability_request_values: normalizeValuesForSubmit(additionalFields, item.capabilityValues, capabilityUploads),
        });
      }

      const needUploads = capabilities.length ? {} : await uploadFilesForNeed();

      const response = await axiosInstance.post('/requests', {
        requesting_business_unit_id: form.requesting_business_unit_id,
        is_cui: classificationIsCui(form.classification),
        title: form.title.trim(),
        description: form.description.trim(),
        needed_by: form.needed_by || undefined,
        priority: form.priority || undefined,
        general_notes: form.general_notes.trim() || undefined,
        general_artifact_ids: generalArtifactIds,
        need_category_id: capabilities.length ? undefined : form.need_category_id,
        need_request_values: capabilities.length
          ? undefined
          : normalizeValuesForSubmit(needFields, needValues, needUploads),
        capabilities,
      });
      const requestId = response.data?.request_id || response.data?.request?.request_id;
      if (!requestId) {
        throw new Error('Request was submitted, but the request id was missing.');
      }
      navigate(`/requests/${requestId}`);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to submit request.');
    } finally {
      setSubmitting(false);
    }
  };

  const generalFileLabel = generalFiles.length
    ? generalFiles.map((file) => file.name).join(', ')
    : 'Upload General Artifacts';

  const reviewItems = selectedCapabilities.map((item) => {
    const itemErrors = capabilityErrors[item.capability_definition_id] || { shared: {}, capability: {} };
    const complete = Object.keys(itemErrors.shared || {}).length === 0 && Object.keys(itemErrors.capability || {}).length === 0;
    return { item, complete };
  });
  const isNeedBased = selectedCapabilities.length === 0;
  const selectedNeedCategory = needCategories.find((category) => category.category_id === form.need_category_id);
  const needReviewItems = needFields.reduce((items, field) => {
    const name = fieldName(field);
    const value = fieldType(field) === 'file' ? needFileValues[name] : needValues[name];
    if (!name || fieldType(field) === 'instruction' || isBlank(value)) {
      return items;
    }
    items.push({ name, label: fieldLabel(field), value: reviewValue(value) });
    return items;
  }, []);

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Header
            breadcrumbItems={[
              { label: 'AMX xConsole', href: '/' },
              { label: 'Requests', href: '/requests' },
              { label: 'Create Request' },
            ]}
          />

          {loading ? (
            <Stack direction="row" spacing={2} alignItems="center">
              <CircularProgress size={24} />
              <Typography>Loading request builder...</Typography>
            </Stack>
          ) : (
            <Stack spacing={3} sx={{ maxWidth: 1120 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>
                    Create Request
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Select known Capabilities, or leave them empty and describe the outcome so providers can propose a solution.
                  </Typography>
                </Box>
                <Button component={RouterLink} to="/requests" startIcon={<ArrowBackRoundedIcon />}>Requests</Button>
              </Stack>

              {form.classification ? (
                <ClassificationBanner isCui={classificationIsCui(form.classification)} />
              ) : null}

              {error ? <Alert severity="error">{error}</Alert> : null}

              {!reviewing ? (
                <>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>Request Details</Typography>
                      <TextField
                        select
                        label="Requesting Business Unit"
                        value={form.requesting_business_unit_id}
                        onChange={(event) => {
                          setForm((current) => ({ ...current, requesting_business_unit_id: event.target.value }));
                          setSelectedCapabilities([]);
                        }}
                        required
                        fullWidth
                        disabled={submitting || businessUnits.length === 1}
                        error={Boolean(formErrors.requesting_business_unit_id)}
                        helperText={formErrors.requesting_business_unit_id || ' '}
                      >
                        {businessUnits.map((unit) => (
                          <MenuItem key={unit.business_unit_id} value={unit.business_unit_id}>
                            {businessUnitLabel(unit)}
                          </MenuItem>
                        ))}
                      </TextField>
                      <RequestClassificationField
                        value={form.classification}
                        onChange={(event) => setForm((current) => ({ ...current, classification: event.target.value }))}
                        disabled={submitting}
                        error={Boolean(formErrors.classification)}
                        helperText={formErrors.classification || 'Select whether this request contains Controlled Unclassified Information.'}
                      />
                      <TextField
                        label="Request Title"
                        value={form.title}
                        onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
                        required
                        fullWidth
                        disabled={submitting}
                        error={Boolean(formErrors.title)}
                        helperText={formErrors.title || ' '}
                      />
                      <TextField
                        label="Description"
                        value={form.description}
                        onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                        required
                        multiline
                        minRows={4}
                        fullWidth
                        disabled={submitting}
                        error={Boolean(formErrors.description)}
                        helperText={formErrors.description || ' '}
                      />
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField
                          label="Needed By"
                          type="date"
                          value={form.needed_by}
                          onChange={(event) => setForm((current) => ({ ...current, needed_by: event.target.value }))}
                          inputProps={{ min: localToday() }}
                          fullWidth
                          disabled={submitting}
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          select
                          label="Priority"
                          value={form.priority}
                          onChange={(event) => setForm((current) => ({ ...current, priority: event.target.value }))}
                          fullWidth
                          disabled={submitting}
                        >
                          <MenuItem value="">None</MenuItem>
                          <MenuItem value="low">Low</MenuItem>
                          <MenuItem value="normal">Normal</MenuItem>
                          <MenuItem value="high">High</MenuItem>
                          <MenuItem value="urgent">Urgent</MenuItem>
                        </TextField>
                      </Stack>
                      <TextField
                        label="General Notes"
                        value={form.general_notes}
                        onChange={(event) => setForm((current) => ({ ...current, general_notes: event.target.value }))}
                        multiline
                        minRows={2}
                        fullWidth
                        disabled={submitting}
                      />
                      <Button
                        variant="outlined"
                        component="label"
                        fullWidth
                        disabled={submitting}
                        startIcon={<UploadFileRoundedIcon />}
                        sx={{ justifyContent: 'flex-start', py: 1.5, borderRadius: 1, borderStyle: 'dashed' }}
                      >
                        {generalFileLabel}
                        <input
                          type="file"
                          hidden
                          multiple
                          onChange={(event) => setGeneralFiles(Array.from(event.target.files || []))}
                        />
                      </Button>
                    </Stack>
                  </Paper>

                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between">
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 800 }}>Capabilities (Optional)</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Add Capabilities when you know the solution. Leave this empty to request provider proposals based on the Need Category.
                          </Typography>
                        </Box>
                        <Button
                          variant="contained"
                          startIcon={<AddRoundedIcon />}
                          disabled={submitting || !form.requesting_business_unit_id}
                          onClick={() => setPickerOpen(true)}
                        >
                          Add Capability
                        </Button>
                      </Stack>
                      {selectedCapabilities.length ? selectedCapabilities.map((item, index) => (
                        <RequestedCapabilityFormCard
                          key={item.capability_definition_id}
                          item={item}
                          index={index}
                          errors={capabilityErrors[item.capability_definition_id]}
                          submitting={submitting}
                          onChange={updateCapabilityValue}
                          onFileChange={updateCapabilityFile}
                          onRemove={removeCapability}
                        />
                      )) : (
                        <Alert severity="info">
                          No Capabilities selected. This request will open as a need-based opportunity for qualified providers.
                        </Alert>
                      )}
                    </Stack>
                  </Paper>

                  {!selectedCapabilities.length ? (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Stack spacing={2}>
                        <Box>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>Need Category & Requirements</Typography>
                            <Chip size="small" color="primary" label="Need-Based Routing" />
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            With no Capability selected, the Need Category routes this request to qualified provider Business Units for proposals.
                          </Typography>
                        </Box>
                        <TextField
                          select
                          label="Need Category"
                          value={form.need_category_id}
                          onChange={(event) => setForm((current) => ({ ...current, need_category_id: event.target.value }))}
                          required
                          fullWidth
                          disabled={submitting}
                          error={Boolean(formErrors.need_category_id)}
                          helperText={formErrors.need_category_id || selectedNeedCategory?.description || 'Used only for provider discovery and routing.'}
                        >
                          {needCategories.map((category) => (
                            <MenuItem key={category.category_id} value={category.category_id}>
                              {category.category_display_name || category.category_name}
                            </MenuItem>
                          ))}
                        </TextField>
                        {needSchemaLoading ? (
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <CircularProgress size={20} />
                            <Typography variant="body2">Loading Need Category requirements...</Typography>
                          </Stack>
                        ) : form.need_category_id ? (
                          <SchemaBodyFields
                            fields={needFields}
                            values={needValues}
                            fileValues={needFileValues}
                            errors={needFieldErrors}
                            disabled={submitting}
                            onChange={(field, value) => setNeedValues((current) => ({ ...current, [fieldName(field)]: value }))}
                            onFileChange={(field, value) => setNeedFileValues((current) => ({ ...current, [fieldName(field)]: value }))}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Select a Need Category to load its request requirements.
                          </Typography>
                        )}
                      </Stack>
                    </Paper>
                  ) : null}

                  <Stack direction="row" justifyContent="flex-end" spacing={1}>
                    <Button
                      variant="contained"
                      startIcon={<CheckCircleRoundedIcon />}
                      disabled={submitting || (isNeedBased && needSchemaLoading)}
                      onClick={() => {
                        if (validateForm()) {
                          setReviewing(true);
                        }
                      }}
                    >
                      Review Request
                    </Button>
                  </Stack>
                </>
              ) : (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>Review Request</Typography>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 800 }}>{form.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {businessUnitLabel(businessUnits.find((unit) => unit.business_unit_id === form.requesting_business_unit_id))}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{form.description}</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        size="small"
                        color={isNeedBased ? 'primary' : 'default'}
                        label={isNeedBased ? 'Need-Based Request' : 'Capability-Based Request'}
                      />
                      {form.needed_by ? <Chip size="small" label={`Needed ${form.needed_by}`} /> : null}
                      {form.priority ? <Chip size="small" label={formatCapabilityLabel(form.priority)} /> : null}
                      <Chip size="small" label={`${generalFiles.length} General Artifact${generalFiles.length === 1 ? '' : 's'}`} />
                    </Stack>
                    <Divider />
                    {isNeedBased ? (
                      <Stack spacing={1.5}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Need Category</Typography>
                          <Typography variant="body2">
                            {selectedNeedCategory?.category_display_name || selectedNeedCategory?.category_name}
                          </Typography>
                        </Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Need Requirements</Typography>
                        {needReviewItems.length ? (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Requirement</TableCell>
                                <TableCell>Response</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {needReviewItems.map((item) => (
                                <TableRow key={item.name}>
                                  <TableCell>{item.label}</TableCell>
                                  <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{item.value}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          <Typography variant="body2" color="text.secondary">No additional Need Category responses.</Typography>
                        )}
                      </Stack>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Capability</TableCell>
                            <TableCell>Provider</TableCell>
                            <TableCell>Need Category</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {reviewItems.map(({ item, complete }) => (
                            <TableRow key={item.capability_definition_id}>
                              <TableCell>{item.contract?.capability_name || item.capability?.capability_name}</TableCell>
                              <TableCell>{businessUnitLabel(item.contract?.provider_business_unit || item.capability?.provider_business_unit || {})}</TableCell>
                              <TableCell>{item.contract?.category_display_name || item.contract?.category_name || 'N/A'}</TableCell>
                              <TableCell>
                                <Chip size="small" color={statusColor(complete)} label={complete ? 'Complete' : 'Incomplete'} />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                    <Stack direction="row" justifyContent="flex-end" spacing={1}>
                      <Button onClick={() => setReviewing(false)} disabled={submitting}>
                        Back
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<SendRoundedIcon />}
                        disabled={submitting}
                        onClick={submitRequest}
                      >
                        {submitting ? 'Submitting...' : 'Submit Request'}
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              )}
            </Stack>
          )}

          <CapabilityPickerDialog
            open={pickerOpen}
            requestingBusinessUnitId={form.requesting_business_unit_id}
            selectedIds={selectedIds}
            onClose={() => setPickerOpen(false)}
            onAdd={addCapability}
          />
        </Box>
      </Box>
    </AppTheme>
  );
}
